import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { api } from '../api/client'
import type { Katalog, Sonderfall, SonderfallEbene as EbeneTyp } from '../api/typen'
import AppRahmen from '../komponenten/AppRahmen'
import ErgebnisFormular, { type ErgebnisWerte } from '../komponenten/ErgebnisFormular'
import Kamera from '../komponenten/Kamera'
import SonderfallSchritte, { ebenenLage } from '../komponenten/SonderfallSchritte'

/**
 * Schritt 3 von 3 im Sonderfall: eine Ebene fotografieren und manuell zuordnen (P7-02, P7-03).
 *
 * <p>Dieselbe Seite dient zwei Wegen. Im Ablauf (?ablauf=1) geht es nach dem Uebernehmen zur
 * naechsten Ebene weiter, von oben nach unten. Aus der Sonderfall-Ansicht heraus laesst sich eine
 * einzelne Ebene neu fotografieren oder anders zuordnen, danach geht es dorthin zurueck.
 */
export default function SonderfallEbene() {
  const { sonderfallId, ebeneNr } = useParams()

  // Der Wechsel von Ebene 1 zu Ebene 2 bleibt dieselbe Route. Ohne eigenen Schluessel wuerden
  // Formularwerte und Fotozustand der vorigen Ebene stehen bleiben.
  return (
    <Ebenenschritt key={`${sonderfallId}-${ebeneNr}`} sonderfallId={sonderfallId!} nummer={Number(ebeneNr)} />
  )
}

function Ebenenschritt({ sonderfallId, nummer }: { sonderfallId: string; nummer: number }) {
  const [suche] = useSearchParams()
  const imAblauf = suche.get('ablauf') === '1'
  const navigiere = useNavigate()
  const abfragen = useQueryClient()

  const [werte, setWerte] = useState<ErgebnisWerte | null>(null)
  const [beruehrt, setBeruehrt] = useState(false)
  const [neuAufnehmen, setNeuAufnehmen] = useState(false)

  const sonderfall = useQuery({
    queryKey: ['sonderfall', sonderfallId],
    queryFn: () => api.get<Sonderfall>(`/api/sonderfaelle/${sonderfallId}`),
  })
  const katalog = useQuery({ queryKey: ['katalog'], queryFn: () => api.get<Katalog>('/api/katalog') })

  const daten = sonderfall.data
  const ebene = daten?.ebenen.find((eintrag) => eintrag.ebeneNr === nummer)
  const anzahl = daten?.anzahlEbenen ?? 0

  // Nachbelegen, solange nichts angetippt wurde - sonst bliebe ein zwischengespeicherter Stand stehen.
  useEffect(() => {
    if (!ebene || beruehrt) return
    setWerte(ausEbene(ebene))
  }, [ebene, beruehrt])

  const zurueckgeben = (aktualisiert: Sonderfall) => {
    abfragen.setQueryData(['sonderfall', sonderfallId], aktualisiert)
    abfragen.invalidateQueries({ queryKey: ['versorgungspunkt', String(aktualisiert.versorgungspunktId)] })
  }

  const foto = useMutation({
    mutationFn: (bild: Blob) =>
      api.bild<Sonderfall>(
        `/api/sonderfaelle/${sonderfallId}/ebenen/${nummer}/foto`,
        bild,
        `ebene-${nummer}.jpg`,
      ),
    onSuccess: (aktualisiert) => {
      zurueckgeben(aktualisiert)
      setNeuAufnehmen(false)
    },
  })

  const zuordnen = useMutation({
    mutationFn: () =>
      api.put<Sonderfall>(`/api/sonderfaelle/${sonderfallId}/ebenen/${nummer}`, {
        waagen: werte?.waagen ?? [],
        boxHoehe: werte?.boxHoehe ?? null,
        fifo: werte?.fifo ?? false,
        fifoRichtung: werte?.fifoRichtung ?? null,
      }),
    onSuccess: (aktualisiert) => {
      zurueckgeben(aktualisiert)
      if (imAblauf && nummer < aktualisiert.anzahlEbenen) {
        navigiere(`/sonderfaelle/${sonderfallId}/ebenen/${nummer + 1}?ablauf=1`, { replace: true })
      } else {
        navigiere(`/sonderfaelle/${sonderfallId}`, { replace: imAblauf })
      }
    },
  })

  const fotoDa = Boolean(ebene?.fotoVorhanden)
  const zeigeKamera = !fotoDa || neuAufnehmen
  // Im Ablauf gehoert zu jeder Ebene ihr Foto (P7-02). Aus der Ansicht heraus darf auch nur die
  // Zuordnung korrigiert werden.
  const uebernehmenMoeglich = Boolean(werte) && (!imAblauf || fotoDa) && !zuordnen.isPending

  const fussText = !imAblauf
    ? 'Übernehmen'
    : nummer < anzahl
      ? 'Ebene übernehmen und weiter'
      : 'Ebene übernehmen und fertig'

  return (
    <AppRahmen
      titel={daten?.bezeichnung ?? 'Sonderfall'}
      unter={imAblauf ? `Schritt 3 von 3 · Ebene ${nummer} von ${anzahl}` : `Ebene ${nummer} von ${anzahl}`}
      zurueck={`/sonderfaelle/${sonderfallId}`}
      pfad={daten?.pfad}
      ebene="sonderfall"
      pfadZusatz={daten ? `${daten.bezeichnung} · Ebene ${nummer}` : undefined}
      fuss={
        <button className="btn btn-primaer" disabled={!uebernehmenMoeglich} onClick={() => zuordnen.mutate()}>
          {fussText}
        </button>
      }
    >
      {imAblauf && <SonderfallSchritte aktuell="ebenen" />}

      <div className="gruppe">
        <h2 className="s">
          Ebene {nummer} · {ebenenLage(nummer, anzahl)}
        </h2>
        <p className="sek">
          Die ganze Ebene aufs Foto, von links nach rechts vollständig im Bild. Danach Waage, Box und Teiler
          von Hand zuordnen.
        </p>
      </div>

      {zeigeKamera ? (
        <>
          <Kamera
            gesperrt={foto.isPending}
            hinweis={foto.isPending ? 'Wird gespeichert …' : undefined}
            beiAufnahme={(bild) => foto.mutate(bild)}
          />
          {neuAufnehmen && (
            <button className="btn-dezent" onClick={() => setNeuAufnehmen(false)}>
              Bisheriges Foto behalten
            </button>
          )}
        </>
      ) : (
        <div className="gruppe">
          <img
            src={`/api/sonderfaelle/${sonderfallId}/ebenen/${nummer}/foto`}
            alt={`Ebene ${nummer}`}
            style={{ borderRadius: 8, border: '1px solid var(--linie)' }}
          />
          <button className="btn-dezent" onClick={() => setNeuAufnehmen(true)}>
            Foto neu aufnehmen
          </button>
        </div>
      )}

      {imAblauf && !fotoDa && (
        <p className="sek">Erst fotografieren, dann lässt sich die Ebene übernehmen.</p>
      )}

      {foto.isError && <p className="hinweiszeile">{(foto.error as Error).message}</p>}

      {werte && katalog.data && (
        <ErgebnisFormular
          werte={werte}
          aendern={(neu) => {
            setBeruehrt(true)
            setWerte(neu)
          }}
          katalog={katalog.data}
          schlank
        />
      )}

      {zuordnen.isError && <p className="hinweiszeile">{(zuordnen.error as Error).message}</p>}
    </AppRahmen>
  )
}

/** Eine noch nicht zugeordnete Ebene startet mit einer vollen Waage - der haeufigste Fall. */
function ausEbene(ebene: EbeneTyp): ErgebnisWerte {
  const zugeordnet = ebene.waagen.length > 0 || ebene.boxHoehe !== null
  if (!zugeordnet) {
    return {
      waagen: ['30x50'],
      boxHoehe: 'H16',
      korbtyp: null,
      fifo: false,
      fifoRichtung: null,
      anzahlArtikel: 1,
    }
  }
  return {
    waagen: ebene.waagen,
    boxHoehe: ebene.boxHoehe,
    // Im Sonderfall gibt es keinen Korbtyp (P7-03).
    korbtyp: null,
    fifo: ebene.fifo,
    fifoRichtung: ebene.fifoRichtung,
    anzahlArtikel: ebene.waagen.length,
  }
}
